/**
 * <park-admin-tile-grid> + <park-admin-tile> — the operator's orientation tile
 * grid, lifted 1:1 from the `.yp-grid`/`.yp-tile` markup that
 * `components/admin/AdminLanding.astro` used to hand-roll (anton/forrest#1000).
 *
 * Single source of truth = the pure `renderParkAdminTileGrid()` function below,
 * consumed by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkAdminTileGrid.astro`,
 *     which calls it server-side and emits the markup inside
 *     <park-admin-tile-grid data-ssr> so the grid is in the initial HTML (no-JS), and
 *   - the Claude-Design preview, where <park-admin-tile-grid> parses its
 *     author-syntax <park-admin-tile> children and renders client-side.
 * Because both call the SAME function, server and client markup are identical.
 *
 * This is the FIRST operator-facing element in the library. It exists because
 * design-system#FR-3 covers operator-facing patterns too (anton/forrest#999) —
 * before that, FR-3 was scoped to public pages only, which is how the admin came
 * to hand-roll every pattern it uses.
 *
 * Author syntax (`chips` is a `|`-separated list — chips are short localized
 * hints like "näkyy /varaa" and never contain a pipe):
 *   <park-admin-tile-grid>
 *     <park-admin-tile
 *       title="Etusivu" desc="Hero, nostot…" chips="näkyy /|näkyy /varaa"
 *       note="Optional warning" href="/_emdash/admin/content/home"
 *       action="Muokkaa" path="content/home"></park-admin-tile>
 *   </park-admin-tile-grid>
 *
 * Styling lives in wc/css/park-admin-tile.css (lifted 1:1 from AdminLanding's
 * scoped <style>) — no <style> here, no shadow DOM.
 *
 * @dsSpec design-system#FR-3 — the recurring operator orientation tile.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-admin-tile-grid";
const TILE_TAG = "park-admin-tile";

export interface ParkAdminTileData {
	title?: string;
	desc?: string;
	/** Short route/scope hints rendered as pills. */
	chips?: string[];
	/** Optional warm-toned caveat under the chips. */
	note?: string;
	href?: string;
	/** Call to action, e.g. "Muokkaa" / "Avaa". */
	action?: string;
	/** Monospace destination hint shown next to the action. */
	path?: string;
}

/** Split the `chips` author attribute into its parts. Empty → no chips. */
export function parseChips(raw?: string): string[] {
	return (raw ?? "")
		.split("|")
		.map((c) => c.trim())
		.filter(Boolean);
}

/**
 * Pure render: (tiles) → inner HTML string. No DOM access, so it is safe to call
 * server-side (the .astro wrapper) and client-side (the element). Emits the whole
 * `<div class="pa-grid">…</div>`. desc/chips/note/path are omitted when empty,
 * matching the conditionals the hand-rolled AdminLanding markup used.
 */
export function renderParkAdminTileGrid(tiles: ParkAdminTileData[]): string {
	const items = tiles
		.map((tile) => {
			const desc = tile.desc
				? html`<p class="pa-tile-what">${esc(tile.desc)}</p>`
				: "";
			const chips = tile.chips?.length
				? html`<div class="pa-chips">${tile.chips.map((c) => html`<span class="pa-chip">${esc(c)}</span>`).join("")}</div>`
				: "";
			const note = tile.note
				? html`<span class="pa-note">${esc(tile.note)}</span>`
				: "";
			const path = tile.path
				? html`<span class="pa-path">${esc(tile.path)}</span>`
				: "";
			const go = tile.action
				? html`<span class="pa-go">${esc(tile.action)} →${path}</span>`
				: "";
			return html`<a class="pa-tile" href="${esc(tile.href ?? "")}"><h3>${esc(tile.title ?? "")}</h3>${desc}${chips}${note}${go}</a>`;
		})
		.join("");
	return html`<div class="pa-grid">${items}</div>`;
}

class ParkAdminTileGrid extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		// Parse the author-syntax <park-admin-tile …> children captured in
		// slotHtml into the tile array, then render via the pure fn.
		const tiles: ParkAdminTileData[] = [];
		const tmp = document.createElement("div");
		tmp.innerHTML = this.slotHtml;
		for (const el of tmp.querySelectorAll(TILE_TAG)) {
			tiles.push({
				title: el.getAttribute("title") ?? undefined,
				desc: el.getAttribute("desc") ?? undefined,
				chips: parseChips(el.getAttribute("chips") ?? undefined),
				note: el.getAttribute("note") ?? undefined,
				href: el.getAttribute("href") ?? undefined,
				action: el.getAttribute("action") ?? undefined,
				path: el.getAttribute("path") ?? undefined,
			});
		}
		return renderParkAdminTileGrid(tiles);
	}
}

// Author-syntax only: parsed by <park-admin-tile-grid>. Extends ParkElement (not
// HTMLElement directly) so the module stays SSR-import-safe via base.ts's guarded
// ElementBase — a direct `extends HTMLElement` crashes the ds/*.astro wrappers.
class ParkAdminTile extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}
	protected render(): string {
		return this.slotHtml;
	}
}

defineElement(TAG, ParkAdminTileGrid);
defineElement(TILE_TAG, ParkAdminTile);

export { ParkAdminTileGrid, ParkAdminTile };
